import {
  AfterViewInit,
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

import { AuthService } from '../../shared/services/auth.service';
import { FileTransferService, MTMCustomFile } from '../../shared/services/signed-url';
import { SubsectionService } from '../../shared/services/subsection.service';
import {
  ActiveSubsectionState,
  AnnotationDeleteMessage,
  AnnotationEditMessage,
  AnnotationHighlightSelectedAnnotation,
  AnnotationSelectThreadNew,
  AnnotationSendMediaMessage,
  AnnotationSendOtherTypesMessage,
  BatchVote,
  CompareOption,
  OptionVersion,
  OptionVersionSimple,
  SimpleFilePage,
  SimpleFilePages,
  SimpleUser,
  SubsectionNavigationParams,
  User,
  UserVote,
} from '../../shared/interfaces';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { OverlayService } from '../../shared/services/overlayService';
import { ProjectService } from '../../shared/services/project.service';
import { AppStateService } from '../../shared/services/app-state.service';
import { TopMediaInfoService } from '../../shared/components/top-media-info/top-media-info.service';
import { ReleaseTreeService } from '../services/release-tree-service';
import { ThemeService } from '../../shared/services/theme.service';
import { CommentAnnotationPlugin } from '@mtm/conversation-client-sdk-annotation';
import { CustomAction } from '@mtm/conversation-ui-angular';
import {
  onAddingAnnotationDataChanged,
  onConversationPluginReady,
  onAnnotationClickClosedV2,
} from '../../shared/services/annotation.service';
import { EmitterService } from '../../shared/services/emitter.service';
import { VoteService } from '../../shared/services/vote.service';
import { AssetsApprovalComponent } from '../approval/assets-approval.component';
import { DRAWING_TOOLS } from '../../shared/models/annotation-tools';

export type FileVote = {
  accepted: number;
  rejected: number;
  userVote: string;
  proponents: UserVote[];
  opponents: UserVote[];
};
import { NavigationRequest } from '@mtm/conversation-ui-angular';
import { PlatformCommentsChatStateService } from '../../shared/services/platform-comments-chat-state.service';
import { GeneralUtilsService } from '../../shared/services/general-utils.service';
import { FileTypeService } from '../../shared/services/file-type.service';
import { MediaPlayerComponent } from '../../shared/components/media-player/media-player.component';

@Component({
  selector: 'subsection-preview',
  templateUrl: './subsection-preview.component.html',
  styleUrl: './subsection-preview.component.scss',
  standalone: false,
})
export class SubsectionPreviewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('assetContainer') assetContainer: any;
  @ViewChild('sliderMediaPlayerComponent') sliderMediaPlayerComponent: MediaPlayerComponent;
  @ViewChild('mediaPreviewCommentsChat') mediaPreviewCommentsChat: TemplateRef<any>;
  @Input() isWidgetMode: boolean = false;
  @Input() shareLinkPreview: boolean = false;
  @Output() pageClick = new EventEmitter<Partial<ActiveSubsectionState>>();

  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly subsectionService = inject(SubsectionService);
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly translatePipe = inject(TranslatePipe);
  private readonly projectService = inject(ProjectService);
  private readonly overlayService = inject(OverlayService);
  private readonly modalService = inject(NgbModal);
  private readonly transferService = inject(FileTransferService);
  readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly releaseTreeService = inject(ReleaseTreeService);
  private readonly fileTypeService = inject(FileTypeService);
  private readonly appStateService = inject(AppStateService);
  private readonly themeService = inject(ThemeService);

  subSectionState: ActiveSubsectionState;
  voteService = inject(VoteService);
  private readonly platformChatState = inject(PlatformCommentsChatStateService);

  isCollapsed: boolean = false;
  commentsCollapsed: boolean = false;
  pdfSliderCollapsed: boolean = false;
  isCommentPanelFullSize: boolean = false;
  versionData: OptionVersion | null = null;
  activeFile: any = null;
  activePage: SimpleFilePage | null = null;
  activePages: SimpleFilePages | null = null;
  versionVotes: Record<string, FileVote> = {};
  currentUsername: string = '';

  //voting helper variables
  isCurrentApproved: boolean = false;
  isCurrentRejected: boolean = false;
  totalApproved: number = 0;
  totalRejected: number = 0;
  currentProponents: UserVote[] = [];
  currentOpponents: UserVote[] = [];
  canVote: boolean = false;

  isLoading: boolean = false;
  projectParticipants: SimpleUser[] = [];
  isAssetListVisible: boolean = false;
  instanceId: string = Math.random().toString(36).substring(2, 15);
  isConvertibleToPages: boolean = false;

  mediaPlayerState: any;
  private releaseRecord: Record<string, any> = {};
  annotationPlugin: CommentAnnotationPlugin;
  pluginReady: boolean = false;
  navigationRequest: NavigationRequest | null = null;
  private pendingNavRequest: NavigationRequest | null = null;
  isChannelManagerVisible: boolean = false; // to hide leave comments comp when channel manager is visible
  isConversationInterfaceBusy: boolean = false;
  highlightedAnnotationId: string | null = null;

  get drawingTools(): CustomAction[] {
    return DRAWING_TOOLS;
  }
  startTime: number = null;
  endTime: number = null;

  get isFullscreen() {
    return this.topMediaInfoService.isFullscreen;
  }

  get isCurrentVersionLocked(): boolean {
    if (!this.subSectionState?.activeVersion?.versionNumber || !this.subSectionState?.activeOption?.optionId) {
      return false;
    }

    // Construct version ID as optionId-versionNumber
    const versionId = `${this.subSectionState.activeOption.optionId}-${this.subSectionState.activeVersion.versionNumber}`;
    return !!this.releaseRecord[versionId];
  }

  /** Aligns with top-nav `hasLockedVersionInCurrentOption` (disable voting when any version in option is locked). */
  get hasLockedVersionInCurrentOption(): boolean {
    if (!this.subSectionState?.activeOption?.simpleVersions || !this.subSectionState?.activeOption?.optionId) {
      return false;
    }
    return this.subSectionState.activeOption.simpleVersions.some((version: OptionVersionSimple) => {
      const versionId = `${this.subSectionState.activeOption.optionId}-${version.versionNumber}`;
      return !!this.releaseRecord[versionId];
    });
  }

  get fileId(): string {
    return this.subSectionState?.activeFile?.id || '';
  }

  get isSubsectionFile(): boolean {
    return this.subSectionState?.versionData?.versionNumber === 0;
  }

  get uploadRecord(): Record<string, MTMCustomFile> {
    return this.transferService.uploadRecord;
  }

  get currentProjectId(): string {
    return this.projectService.project?.id || '';
  }

  get activeFileMediaType(): string | null {
    if (!this.activeFile) {
      return undefined;
    }

    const file = this.activeFile;
    if (this.fileTypeService.isVideoFile(file)) {
      return 'video';
    } else if (this.fileTypeService.isImageFile(file)) {
      return 'image';
    } else if (this.fileTypeService.isAudioFile(file)) {
      return 'audio';
    } else {
      return this.activePage ? 'image' : 'general';
    }
  }

  get duration(): number | null {
    const playerComponent = this.sliderMediaPlayerComponent;
    if (!playerComponent) {
      return null;
    }

    const { mediaType } = playerComponent;
    if (mediaType === 'video' && playerComponent.player) {
      const videoDuration = playerComponent.player.duration?.();
      return Number.isNaN(videoDuration) ? null : videoDuration;
    }

    if (mediaType === 'audio') {
      if (playerComponent.enableAudioAnnotation && playerComponent.audioAnnotationComponent?.wavesurfer) {
        const waveSurferDuration = playerComponent.audioAnnotationComponent.wavesurfer.getDuration?.();
        return Number.isNaN(waveSurferDuration) ? null : waveSurferDuration;
      }

      const audioDuration = playerComponent.mtmAudioPlayer?.nativeElement?.duration;
      return Number.isNaN(audioDuration) ? null : audioDuration;
    }

    return null;
  }

  constructor(private readonly router: Router) {
    effect(() => {
      this.appStateService.commentPanelFullSize();
      this.updateCommentPanelFullSize();
    });
  }

  private updateCommentPanelFullSize(): void {
    const isFullSize = this.appStateService.getCommentPanelFullSize();
    if (!this.commentsCollapsed) {
      this.isCommentPanelFullSize = isFullSize;
    } else {
      this.isCommentPanelFullSize = false;
    }
  }

  private resetComponent() {
    this.pluginReady = false;
    this.annotationPlugin = null as any;
    this.highlightedAnnotationId = null;
    this.appStateService.setCommentPluginReady(false);
    this.pendingNavRequest = null;
    this.navigationRequest = null;
  }

  ngOnInit() {
    this.topMediaInfoService
      .state$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state) => {
        this.pdfSliderCollapsed = state?.isPdfSliderCollapsed ?? false;
      });
    this.subsectionService.ensureCommentsPanelOpenOnFirstLoad();
    this.appStateService.patchState({ enableCommentPage: false });
    this.appStateService.patchState({ enableTaskPage: false });
    this.currentUsername = this.authService.getAuthUserName();

    const pendingRequest = this.platformChatState.getPendingNavigationRequest() as any;
    if (pendingRequest && (!pendingRequest.targetId || pendingRequest.targetId === this.fileId)) {
      const consumed = this.platformChatState.consumePendingNavigationRequest();
      if (consumed) {
        this.applyOrDeferNavRequest(consumed);
      }
    }

    this.platformChatState.navigationRequest$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((request) => {
      this.applyOrDeferNavRequest(request);
    });

    this.projectParticipants = this.projectService.project?.participants;
    this.canVote = this.projectService.project?.validators?.includes(this.currentUsername);

    // Subscribe to release tree service to get release record
    this.releaseTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (state) => {
          this.releaseRecord = state.releaseRecord || {};
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged(
          (prev, curr) =>
            prev?.activeOption?.optionId === curr?.activeOption?.optionId &&
            prev?.activeVersion?.versionNumber === curr?.activeVersion?.versionNumber
        )
      )
      .subscribe({
        next: (state) => {
          this.subSectionState = state;
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.versionData === curr?.versionData)
      )
      .subscribe({
        next: (state) => {
          this.versionData = state?.versionData;
          this.populateVotes();
          this.syncCurrentVotingState();
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged(
          (prev, curr) =>
            prev?.activeFile === curr?.activeFile &&
            prev.activePage === curr.activePage &&
            prev.activeFilePages === curr.activeFilePages
        )
      )
      .subscribe({
        next: (state) => {
          this.activeFile = state?.activeFile;
          this.activePages = state?.activeFilePages;
          this.activePage = state?.activePage;
          this.updateActiveFileState();
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged(
          (prev, curr) => prev?.activeFilePages === curr?.activeFilePages && prev?.activePage === curr?.activePage
        )
      )
      .subscribe({
        next: (state) => {
          this.subSectionState = state;
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.activePage === curr?.activePage)
      )
      .subscribe({
        next: (state) => {
          this.resetComponent();
          this.subSectionState = state;
          this.isAssetListVisible = false;
          this.syncCurrentVotingState();
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.activeFile === curr?.activeFile)
      )
      .subscribe({
        next: (state) => {
          // Reset plugin ready state when file changes
          if (this.subSectionState?.activeFile?.id !== state?.activeFile?.id) {
            this.resetComponent();
          }
          this.subSectionState = state;
          this.isAssetListVisible = false;
          this.syncCurrentVotingState();
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.explorerCollapsed === curr?.explorerCollapsed)
      )
      .subscribe({
        next: (state) => {
          this.isCollapsed = state?.explorerCollapsed || false;
        },
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.commentsCollapsed === curr?.commentsCollapsed)
      )
      .subscribe({
        next: (state) => {
          this.commentsCollapsed = state?.commentsCollapsed;
          this.updateCommentPanelFullSize();
        },
      });

    EmitterService.get(AnnotationHighlightSelectedAnnotation)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data: {
          instanceId?: string;
          itemId?: string;
          comment?: any;
          annotationId?: string;
          open?: boolean;
          unHighlightComment?: any;
        }) => {
          if (data.instanceId !== this.instanceId || data.itemId !== this.fileId) return;
          if (data.open && (data.annotationId ?? data.comment?.annotation?.id)) {
            this.highlightedAnnotationId = data.annotationId ?? data.comment?.annotation?.id ?? null;
          } else if (data.unHighlightComment) {
            this.highlightedAnnotationId = null;
          }
        }
      );

    onAnnotationClickClosedV2.pipe(takeUntil(this.ngUnsubscribe)).subscribe((args: any) => {
      if (args.instanceId !== this.instanceId || args.itemId !== this.fileId) {
        return;
      }
      this.highlightedAnnotationId = null;
    });

    onConversationPluginReady.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
      // Only set plugin if it matches the current instance
      if (event.instanceId === this.instanceId) {
        this.annotationPlugin = event.annotationPlugin;
        this.pluginReady = true;
        this.appStateService.setCommentPluginReady(true);
        this.themeService.updateContent({
          mediaPreviewCommentsChat: this.mediaPreviewCommentsChat,
        });

        const pendingRequest = this.platformChatState.getPendingNavigationRequest() as any;
        if (pendingRequest && (!pendingRequest.targetId || pendingRequest.targetId === this.fileId)) {
          this.navigationRequest = this.platformChatState.consumePendingNavigationRequest();
          this.pendingNavRequest = null;
        } else if (this.pendingNavRequest) {
          this.navigationRequest = this.pendingNavRequest;
          this.pendingNavRequest = null;
        }
      }
    });

    onAddingAnnotationDataChanged.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: any) => {
      this.startTime = event.data?.range.start || event.range?.start;
      this.endTime = event.data?.range.end || event.range?.end;
    });
  }

  ngAfterViewInit() {
    window['subsectionPreview'] = this;
    this.topMediaInfoService.updateState({ subsectionPreview: this });
    this.themeService.updateContent({
      mediaPreviewCommentsChat: this.mediaPreviewCommentsChat,
    });
  }

  private applyOrDeferNavRequest(request: any): void {
    if (request && request.targetId && request.targetId !== this.fileId) {
      return;
    }
    if (this.pluginReady) {
      this.navigationRequest = request;
      this.platformChatState.consumePendingNavigationRequest();
    } else {
      this.pendingNavRequest = request;
    }
  }

  ngOnDestroy() {
    this.appStateService.patchState({ enableCommentPage: true });
    this.appStateService.patchState({ enableTaskPage: true });
    this.appStateService.setCommentPluginReady(false);
    this.pluginReady = false;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.themeService.updateContent({
      mediaPreviewCommentsChat: null,
    });
  }

  setCollapsed(isCollapsed: boolean) {
    this.subsectionService.updateUIState({ explorerCollapsed: isCollapsed });
  }

  toggleComments() {
    this.subsectionService.updateUIState({ commentsCollapsed: !this.commentsCollapsed });
  }

  onPlayerStateChanged(e: any) {
    this.mediaPlayerState = e;
  }

  showComparison(viewMode: CompareOption = CompareOption.SIDE_BY_SIDE) {
    const currentInfo: SubsectionNavigationParams = {
      projectId: this.versionData?.project,
      sectionId: this.versionData?.section,
      subsectionId: this.versionData?.subsection,
      optionId: this.versionData?.optionId,
      topicId: this.subSectionState?.activeTopic?.topicId,
      versionNumber: this.versionData?.versionNumber,
      fileId: this.subSectionState.activeFile?.id,
    };

    this.router.navigate([`/projects/${this.versionData.project}/compare`], {
      queryParams: {
        projectId: this.versionData.project,
        sectionId: this.versionData.section,
        subsectionId: this.versionData.subsection,
        optionId: this.versionData.optionId,
        version: this.subSectionState.activeVersion.versionNumber,
        file: this.subSectionState.activeFile.id,
        viewMode,
      },
    });
  }

  quitComparison() {
    this.subsectionService.navigateSubsection({
      projectId: this.versionData?.project,
      sectionId: this.versionData?.section,
      subsectionId: this.versionData?.subsection,
      optionId: this.versionData?.optionId,
      topicId: this.subSectionState?.activeTopic?.topicId,
      versionNumber: this.versionData?.versionNumber,
      fileId: this.subSectionState.activeFile?.id,
    });
  }

  updateActiveFileState() {
    if (this.activeFile) {
      const file = this.activeFile;
      this.isConvertibleToPages =
        (file?.displayName?.endsWith('pdf') ||
          this.fileTypeService.isPowerPointFile(file) ||
          this.fileTypeService.isKeynoteFile(file)) &&
        !!this.activePages;
    } else {
      this.isConvertibleToPages = false;
    }
  }

  onPageClick(state: Partial<ActiveSubsectionState>) {
    this.pageClick.emit(state);
  }

  onSelectThread(event: any) {
    // todo: tempor fix to make leave-comment in current FE
    EmitterService.get(AnnotationSelectThreadNew).emit(event);
  }

  onEditMessage(event: any) {
    // todo: tempor fix to make leave-comment in current FE
    EmitterService.get(AnnotationEditMessage).emit(event);
  }

  onDeleteMessage(event: any) {
    // todo: tempor fix to make leave-comment in current FE
    EmitterService.get(AnnotationDeleteMessage).emit(event);
  }

  onShowChannelManager(event: any) {
    this.isChannelManagerVisible = event;
  }

  onCloseChannelManager() {
    const current = !!this.commentsCollapsed;
    if (!current) {
      this.subsectionService.updateUIState({ commentsCollapsed: true });
    }
  }

  private sendEditedComment(comment: any): void {
    if (['video', 'audio', 'image'].includes(this.activeFileMediaType)) {
      EmitterService.get(AnnotationSendMediaMessage).emit({
        message: comment.editingMessage,
        newFiles: [],
        type: 'edit',
        comment: comment,
        instanceId: this.instanceId,
      });
    } else {
      if (!comment.editingMessage) return;
      // if (!comment.isMessageEdit || HelperService.isObjectEmpty(comment.editingMessage)) {
      //   return;
      // }
      this.annotationPlugin.fire('updateComment', {
        annotationId: comment.annotation.id,
        commentId: comment.comment.id,
        body: comment.editingMessage,
        files: [],
      });
    }
  }

  public handleUpdateEditMessage(event: any): void {
    event.comment.editingMessage = event.message;
    this.sendEditedComment(event.comment);
  }

  public handleSendVideoMessage(event: any): void {
    EmitterService.get(AnnotationSendMediaMessage).emit(event);
  }

  public handleSendImageMessage(event: any): void {
    EmitterService.get(AnnotationSendMediaMessage).emit(event);
  }

  public handleSendAudioMessage(event: any): void {
    EmitterService.get(AnnotationSendMediaMessage).emit(event);
  }

  public handleSendOtherTypesMessage(event: any): void {
    EmitterService.get(AnnotationSendOtherTypesMessage).emit(event);
  }

  public handleToolSelect(event: any): void {
    EmitterService.get('changeActiveTool').emit(event);
  }

  public handleColorSelect(event: any): void {
    EmitterService.get('changeColor').emit(event);
  }

  public onStartTimeRangeChange(event: any): void {
    EmitterService.get('startRangeUpdate').emit(event);
  }

  public onEndTimeRangeChange(event: any): void {
    EmitterService.get('endRangeUpdate').emit(event);
  }

  //#region voting methods

  vote(isApproval: boolean = true) {
    if (!this.canVote) {
      return;
    }

    if (!this.isSubsectionFile) {
      this.showApprovalModal(isApproval);
      return;
    }

    this.voteSubsectionFile(isApproval);
  }

  voteSubsectionFile(isApproval: boolean = true) {
    if (isApproval) {
      this.submitApproval();
    } else {
      this.submitRejection();
    }
  }

  showApprovalModal(isApproval: boolean = true) {
    if (!this.versionData) {
      return;
    }

    const modalRef = this.modalService.open(AssetsApprovalComponent, {
      modalDialogClass: 'responsive-modal',
      backdrop: 'static',
      keyboard: false,
    });
    if (!isApproval) {
      modalRef.componentInstance.defaultVote = 'NO';
    }
    modalRef.result.then((result) => {
      this.populateVotes();
      this.syncCurrentVotingState();
    });
  }

  submitApproval() {
    if (this.isLoading || !this.fileId) {
      return;
    }
    this.voteFile('YES');
  }

  submitRejection() {
    if (this.isLoading || !this.fileId) {
      return;
    }
    this.voteFile('NO');
  }

  public cancelVote(): void {
    if (this.isLoading || !this.fileId) {
      return;
    }
    this.voteFile('NA');
  }

  voteFile(value: string) {
    if (!this.subSectionState.activeFile) {
      return;
    }

    this.isLoading = true;
    const vote: BatchVote = {
      projectId: this.versionData.project,
      sectionId: this.versionData.section,
      subsectionId: this.versionData.subsection,
      optionId: this.versionData.optionId,
      votes: {
        [this.subSectionState.activeFile.id]: value,
      },
      version: this.versionData.versionNumber,
    };

    this.voteService
      .sendBatchVote(vote)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.handleVoteResponse(value);
          this.isLoading = false;
        },
        error: (error) => {
          this.overlayService.showError(this.translatePipe.transform('edit_vote_voteFailed'));
          this.isLoading = false;
        },
      });
  }

  private populateVotes() {
    this.versionVotes = {};

    if (!this.versionData?.votesBatch) {
      return;
    }

    Object.entries(this.versionData.votesBatch).forEach(([fileId, votes]) => {
      const fileVote: FileVote = {
        accepted: 0,
        rejected: 0,
        userVote: 'NA',
        proponents: [],
        opponents: [],
      };

      votes.forEach((vote) => {
        if (vote.vote === 'YES') {
          fileVote.accepted++;
          fileVote.proponents.push(vote);
        } else if (vote.vote === 'NO') {
          fileVote.rejected++;
          fileVote.opponents.push(vote);
        }

        if (vote.username === this.currentUsername) {
          fileVote.userVote = vote.vote;
        }
      });

      this.versionVotes[fileId] = fileVote;
    });
  }

  private handleVoteResponse(value: string) {
    const fileId = this.subSectionState.activeFile.id;
    const { versionVotes } = this;
    const { versionData } = this;

    if (!(fileId in versionVotes)) {
      versionVotes[fileId] = {
        accepted: 0,
        rejected: 0,
        userVote: 'NA',
        proponents: [],
        opponents: [],
      };
    }

    const authUser = this.authService.getAuthUser() as User;
    versionVotes[fileId].userVote = value;

    if (!versionData?.votesBatch) {
      versionData.votesBatch = {};
    }

    if (!(fileId in versionData?.votesBatch)) {
      versionData.votesBatch[fileId] = [];
    }

    const batchData = versionData.votesBatch[fileId].find((vote) => vote.username === this.currentUsername);
    if (batchData) {
      batchData.vote = value;
    } else {
      versionData.votesBatch[fileId].push({
        username: this.currentUsername,
        vote: value,
        avatarUrl: authUser.avatarUrl,
        fullName: authUser.fullName,
        votedAt: Date.now(),
        blocked: false,
      });
    }

    switch (value) {
      case 'YES':
        if (!versionVotes[fileId].proponents.some((v) => v.username === this.currentUsername)) {
          versionVotes[fileId].proponents.push({
            username: this.currentUsername,
            vote: 'YES',
            avatarUrl: authUser.avatarUrl,
            fullName: authUser.fullName,
            votedAt: Date.now(),
            blocked: false,
          });
        }
        versionVotes[fileId].opponents = versionVotes[fileId].opponents.filter(
          (voter) => voter.username !== this.currentUsername
        );
        break;
      case 'NO':
        if (!versionVotes[fileId].opponents.some((v) => v.username === this.currentUsername)) {
          versionVotes[fileId].opponents.push({
            username: this.currentUsername,
            vote: 'NO',
            avatarUrl: authUser.avatarUrl,
            fullName: authUser.fullName,
            votedAt: Date.now(),
            blocked: false,
          });
        }
        versionVotes[fileId].proponents = versionVotes[fileId].proponents.filter(
          (voter) => voter.username !== this.currentUsername
        );
        break;
      case 'NA':
        versionVotes[fileId].proponents = versionVotes[fileId].proponents.filter(
          (voter) => voter.username !== this.currentUsername
        );
        versionVotes[fileId].opponents = versionVotes[fileId].opponents.filter(
          (voter) => voter.username !== this.currentUsername
        );
        break;
    }
    versionVotes[fileId].accepted = versionVotes[fileId].proponents.length;
    versionVotes[fileId].rejected = versionVotes[fileId].opponents.length;
    this.syncCurrentVotingState();
  }

  private syncCurrentVotingState() {
    if (!this.fileId) {
      return;
    }

    const { fileId } = this;
    const fileVote = this.versionVotes[fileId];
    if (!fileVote) {
      this.isCurrentApproved = false;
      this.isCurrentRejected = false;
      this.totalApproved = 0;
      this.totalRejected = 0;
      this.currentOpponents = [];
      this.currentProponents = [];
      return;
    }
    this.isCurrentApproved = fileVote.userVote === 'YES';
    this.isCurrentRejected = fileVote.userVote === 'NO';
    this.totalApproved = fileVote.accepted;
    this.totalRejected = fileVote.rejected;
    this.currentProponents = fileVote.proponents;
    this.currentOpponents = fileVote.opponents;
  }

  //#endregion
}
